import { ThemeEvents } from '@theme/events';

/**
 * <pdp-complementary> — "Passt gut dazu" horizontal product slider.
 *
 * Binds the prev/next arrows (smooth scrollBy) and, on desktop (>= 768px), bleeds the
 * scroll track to the viewport's right edge so the last card peeks out — mirroring the
 * mobile full-bleed slider. The track sits in a width-constrained PDP column whose left
 * offset is layout-dependent, so the bleed amount can't be expressed in pure CSS; it is
 * measured here and applied as a negative right margin.
 *
 * With [data-hide-sold-out] (block setting, default on) the element hides while the
 * selected variant is unavailable: the initial state is server-rendered (hidden attr),
 * this class keeps it in sync on variant:update. The element is NOT part of the
 * pdp-variant-fragments morph, so the toggled attribute survives variant switches.
 *
 * Works in the quickview sheet automatically: the element upgrades when the fetched PDP
 * is inserted into the DOM (connectedCallback).
 */
class PdpComplementary extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {Element | null} */
  #variantScope = null;

  connectedCallback() {
    // Same scoping as media-gallery.js: the section (or quickview dialog) the
    // element lives in — keeps main-PDP and quickview instances independent.
    if (this.hasAttribute('data-hide-sold-out')) {
      this.#variantScope = this.closest('.shopify-section, dialog');
      this.#variantScope?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    }

    this.#track = this.querySelector('.pdp-complementary__track');
    if (!this.#track) return;

    this.#bleedRight();
    window.addEventListener('resize', this.#onResize);

    this.querySelector('.pdp-complementary__arrow[data-direction="prev"]')?.addEventListener('click', this.#prev);
    this.querySelector('.pdp-complementary__arrow[data-direction="next"]')?.addEventListener('click', this.#next);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#onResize);
    this.#variantScope?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#variantScope = null;
  }

  /**
   * Hide while the selected variant is unavailable. A missing resource means
   * the option combination has no variant — treated as unavailable too.
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    const variant = event.detail?.resource;
    const available = Boolean(variant && variant.available);
    this.hidden = !available;
    // The bleed measurement is stale after display:none — remeasure on reveal.
    if (available) this.#bleedRight();
  };

  #bleedRight = () => {
    const track = this.#track;
    if (!track) return;
    track.style.marginRight = '';
    if (window.innerWidth >= 768) {
      const bleed = document.documentElement.clientWidth - track.getBoundingClientRect().right;
      if (bleed > 0) track.style.marginRight = `${-bleed}px`;
    }
  };

  #onResize = () => this.#bleedRight();

  #step() {
    return Math.max((this.#track?.clientWidth ?? 0) * 0.8, 200);
  }

  #prev = () => this.#track?.scrollBy({ left: -this.#step(), behavior: 'smooth' });
  #next = () => this.#track?.scrollBy({ left: this.#step(), behavior: 'smooth' });
}

if (!customElements.get('pdp-complementary')) {
  customElements.define('pdp-complementary', PdpComplementary);
}
